import React, { useState, useEffect } from 'react';
import { ActionButton } from './EnhancedFloatingButtons.style';
import { TextButtonBaseProps } from '@oneaudi/unified-web-components/dist/cjs/components/text-button';
import { FloatingButtonActionType } from './types';

interface AnimatedActionButtonProps {
  position: string;
  isDisabled?: boolean;
  onClick: () => void;
  variant: 'primary' | 'secondary' | 'tertiary';
  size: TextButtonBaseProps['size'];
  isVisible: boolean;
  animationDelay: string;
  children: React.ReactNode;
  href?: string;
  type: FloatingButtonActionType;
}

/**
 * Wrapper component for ActionButton that handles individual animation and DOM removal
 */
export const AnimatedActionButton: React.FC<AnimatedActionButtonProps> = ({
  position,
  isDisabled,
  onClick,
  variant,
  size,
  isVisible,
  animationDelay,
  children,
  href,
  type,
}) => {
  const [shouldRender, setShouldRender] = useState(isVisible);
  const [prevIsVisible, setPrevIsVisible] = useState(isVisible);

  // Adjust state when isVisible changes to true immediately during render
  if (isVisible && !prevIsVisible) {
    setPrevIsVisible(true);
    setShouldRender(true);
  } else if (!isVisible && prevIsVisible) {
    setPrevIsVisible(false);
  }

  useEffect(() => {
    let timer: NodeJS.Timeout;

    // Only start the unmount timer if isVisible is false but we are still rendering
    if (!isVisible && shouldRender) {
      const delay = parseFloat(animationDelay) * 1000;
      const fadeOutTime = 300; // matching CSS animation duration
      const buffer = 100; // safety buffer

      timer = setTimeout(
        () => {
          setShouldRender(false);
        },
        delay + fadeOutTime + buffer,
      );
    }

    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [isVisible, shouldRender, animationDelay]);

  // Don't render button at all if it shouldn't be shown
  if (!shouldRender) {
    return null;
  }

  const sharedProps = { position, isDisabled, onClick, variant, size, isVisible, animationDelay };

  if (href !== undefined) {
    return (
      <ActionButton
        {...sharedProps}
        href={href}
        target={type === FloatingButtonActionType.EXTERNAL_LINK ? '_blank' : undefined}
      >
        {children}
      </ActionButton>
    );
  }

  return <ActionButton {...sharedProps}>{children}</ActionButton>;
};
